import React, { useEffect, useState, useRef } from "react";
import {
  Card,
  Space,
  Input,
  Form,
  Button,
  notification,
  Tabs,
  Table,
  message,
  Tooltip,
  Select,
  Switch,
  Tag,
} from "antd";
import { useParams, useNavigate, Link } from "react-router-dom";
import { TestSetService } from "../../services/TestSetService";
import { usePermissions } from "../../PermissionsProvider";
import { IntentGroup, TestSet } from "../../model_gen/evaluation";
import { ChatCompletionRequest } from "../../model_gen/chat";
import {
  ClearOutlined,
  CopyOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { IntentService } from "../../services/IntentService";
import TestRequestEdit from "./TestRequestEdit";
import { RequestViewButton } from "../shared/button/Buttons";

// Add new interface for tag distribution
interface TagDistribution {
  tag: string;
  count: number;
}

const TestSetEdit: React.FC = () => {
  const { intentGroupId, testSetId } = useParams();
  const { apiKey } = usePermissions();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [intentGroup, setIntentGroup] = useState<IntentGroup | null>(null);
  const [testSet, setTestSet] = useState<TestSet | null>(null);
  const [currentEligiblePage, setCurrentEligiblePage] = useState<number>(1);
  const [currentAddedPage, setCurrentAddedPage] = useState<number>(1);
  const [eligiblePageSize, setEligiblePageSize] = useState<number>(10);
  const [addedPageSize, setAddedPageSize] = useState<number>(10);
  const [eligibleRequests, setEligibleRequests] = useState<
    ChatCompletionRequest[]
  >([]);
  const [addedRequests, setAddedRequests] = useState<ChatCompletionRequest[]>(
    []
  );
  const [totalEligible, setTotalEligible] = useState<number>(0);
  const [totalAdded, setTotalAdded] = useState<number>(0);
  const [isEligibleLoading, setIsEligibleLoading] = useState<boolean>(false);
  const [isAddedLoading, setIsAddedLoading] = useState<boolean>(false);
  const addedRequestsCacheRef = useRef<{
    [page: number]: ChatCompletionRequest[];
  }>({});
  const eligibleRequestsCacheRef = useRef<{
    [page: number]: ChatCompletionRequest[];
  }>({});
  const [selectedEligibleRequests, setSelectedEligibleRequests] = useState<
    ChatCompletionRequest[]
  >([]);
  const [selectedAddedRequests, setSelectedAddedRequests] = useState<
    ChatCompletionRequest[]
  >([]);
  const [activeTab, setActiveTab] = useState<string>("eligible");
  const [showOnlySelectedEligible, setShowOnlySelectedEligible] =
    useState(false);
  const [showOnlySelectedAdded, setShowOnlySelectedAdded] = useState(false);
  const [addedRefreshTrigger, setAddedRefreshTrigger] = useState(0);
  const [eligibleRefreshTrigger, setEligibleRefreshTrigger] = useState(0);
  const [tagDistribution, setTagDistribution] = useState<TagDistribution[]>([]);
  const [initialLoadDate] = useState<number>(Date.now());
  const [sessionIdFilter, setSessionIdFilter] = useState<string>("");
  const [appliedSessionIdFilter, setAppliedSessionIdFilter] =
    useState<string>("");
  const [queryFilter, setQueryFilter] = useState<string>("");
  const [isSearchMode, setIsSearchMode] = useState<boolean>(false);
  const [searchResults, setSearchResults] = useState<ChatCompletionRequest[]>(
    []
  );
  const [isSearchLoading, setIsSearchLoading] = useState<boolean>(false);

  useEffect(() => {
    if (intentGroupId) {
      IntentService.getIntentGroup(
        Number(intentGroupId),
        false,
        setIsLoading,
        apiKey
      ).subscribe((intentGroup: IntentGroup) => {
        setIntentGroup(intentGroup);
      });
    }
  }, [intentGroupId]);

  useEffect(() => {
    if (testSetId) {
      TestSetService.getTestSet(testSetId, setIsLoading, apiKey).subscribe(
        (data: TestSet) => {
          setTestSet(data);
          form.setFieldsValue({
            name: data.name,
            description: data.description,
            is_golden: data.is_golden,
          });

          TestSetService.getTestSetRequests(
            testSetId,
            -1,
            -1,
            0,
            10,
            null,
            apiKey
          ).subscribe(
            (response: { items: ChatCompletionRequest[] }) => {
              setAddedRequests(response.items);
              setIsLoading(false);
            },
            (error: any) => {
              console.error(error);
              notification.error({
                message: "Error",
                description: `Failed to fetch test set requests: ${error}`,
              });
              setIsLoading(false);
            }
          );
        },
        (error: any) => {
          console.error(error);
          notification.error({
            message: "Error",
            description: `Failed to fetch test set: ${error}`,
          });
          setIsLoading(false);
        }
      );
    }
  }, [testSetId]);

  useEffect(() => {
    if (intentGroupId && testSetId) {
      fetchEligibleRequests(currentEligiblePage);
    }
  }, [
    currentEligiblePage,
    intentGroupId,
    eligibleRefreshTrigger,
    eligiblePageSize,
  ]);

  useEffect(() => {
    if (testSetId) {
      fetchAddedRequests(currentAddedPage);
    }
  }, [currentAddedPage, testSetId, addedRefreshTrigger, addedPageSize]);

  useEffect(() => {
    if (testSetId) {
      TestSetService.getTestSetRequestDistribution(
        testSetId,
        null,
        apiKey
      ).subscribe(
        (response: TagDistribution[]) => {
          setTagDistribution(response);
        },
        (error: any) => {
          console.error(error);
          notification.error({
            message: "Error",
            description: `Failed to fetch tag distribution: ${error}`,
          });
        }
      );
    }
  }, [testSetId, addedRefreshTrigger]);

  const handleSearch = () => {
    // If in search mode, clear it first
    if (isSearchMode) {
      setIsSearchMode(false);
      setSearchResults([]);
    }

    setAppliedSessionIdFilter(sessionIdFilter);
    setCurrentEligiblePage(1);
    eligibleRequestsCacheRef.current = {};
    setEligibleRefreshTrigger((prev) => prev + 1);
  };

  const handleQuerySearch = () => {
    if (!queryFilter.trim()) {
      // If empty query, reset to normal mode
      clearSearch();
      return;
    }

    // Clear session filter when doing a query search
    setSessionIdFilter("");
    setAppliedSessionIdFilter("");

    setIsSearchLoading(true);
    setIsSearchMode(true);

    TestSetService.searchEligibleTestSetRequests(
      Number(intentGroupId),
      Number(testSetId),
      queryFilter,
      null,
      apiKey
    ).subscribe(
      (items: ChatCompletionRequest[]) => {
        const updatedItems = items.map((request) => {
          const selectedVersion = selectedEligibleRequests.find(
            (selected) => selected.request_id === request.request_id
          );
          return selectedVersion || request;
        });

        setSearchResults(updatedItems);

        // Reset to first page when searching
        setCurrentEligiblePage(1);

        // Calculate the items for the current page
        const startIndex = 0;
        const endIndex = Math.min(eligiblePageSize, updatedItems.length);
        setEligibleRequests(updatedItems.slice(startIndex, endIndex));

        setTotalEligible(updatedItems.length);
        setIsSearchLoading(false);
      },
      (error: any) => {
        console.error(error);
        notification.error({
          message: "Error",
          description: `Failed to search requests: ${error}`,
        });
        setIsSearchLoading(false);
      }
    );
  };

  const clearSearch = () => {
    setQueryFilter("");
    setIsSearchMode(false);
    setSearchResults([]);
    setCurrentEligiblePage(1);
    eligibleRequestsCacheRef.current = {};
    setEligibleRefreshTrigger((prev) => prev + 1);

    // Also clear session filter if it exists
    if (sessionIdFilter) {
      setSessionIdFilter("");
      setAppliedSessionIdFilter("");
    }
  };

  const fetchEligibleRequests = (page: number) => {
    console.log("Fetching eligible requests for page:", page);

    // If in search mode, don't fetch from the regular endpoint
    if (isSearchMode) {
      return;
    }

    if (eligibleRequestsCacheRef.current[page]) {
      const updatedCache = eligibleRequestsCacheRef.current[page].map(
        (cachedRequest) => {
          const selectedVersion = selectedEligibleRequests.find(
            (selected) => selected.request_id === cachedRequest.request_id
          );
          return selectedVersion || cachedRequest;
        }
      );
      setEligibleRequests(updatedCache);
      return;
    }

    const offset = (page - 1) * eligiblePageSize;
    setIsEligibleLoading(true);

    TestSetService.getEligibleTestSetRequests(
      Number(intentGroupId),
      Number(testSetId),
      Number(initialLoadDate),
      offset,
      eligiblePageSize,
      appliedSessionIdFilter || null,
      null,
      apiKey
    ).subscribe(
      (response: { items: ChatCompletionRequest[]; total: number }) => {
        const updatedItems = response.items.map((request) => {
          const selectedVersion = selectedEligibleRequests.find(
            (selected) => selected.request_id === request.request_id
          );
          return selectedVersion || request;
        });

        setEligibleRequests(updatedItems);
        setTotalEligible(response.total);
        eligibleRequestsCacheRef.current = {
          ...eligibleRequestsCacheRef.current,
          [page]: updatedItems,
        };
        setIsEligibleLoading(false);
      },
      (error: any) => {
        console.error(error);
        notification.error({
          message: "Error",
          description: `Failed to fetch eligible requests: ${error}`,
        });
        setIsEligibleLoading(false);
      }
    );
  };

  const fetchAddedRequests = (page: number) => {
    if (addedRequestsCacheRef.current[page]) {
      setAddedRequests(addedRequestsCacheRef.current[page]);
      return;
    }

    const offset = (page - 1) * addedPageSize;
    setIsAddedLoading(true);

    TestSetService.getTestSetRequests(
      testSetId!,
      -1,
      -1,
      offset,
      addedPageSize,
      null,
      apiKey
    ).subscribe(
      (response: { items: ChatCompletionRequest[]; total: number }) => {
        setAddedRequests(response.items);
        setTotalAdded(response.total);
        addedRequestsCacheRef.current = {
          ...addedRequestsCacheRef.current,
          [page]: response.items,
        };
        setIsAddedLoading(false);
      },
      (error: any) => {
        console.error(error);
        notification.error({
          message: "Error",
          description: `Failed to fetch added requests: ${error}`,
        });
        setIsAddedLoading(false);
      }
    );
  };

  const handleTagsChange = (requestId: string, newTags: string[]) => {
    if (activeTab === "added") {
      setIsAddedLoading(true);

      TestSetService.updateTestSetRequestTags(
        testSetId!,
        requestId,
        newTags,
        null,
        apiKey
      ).subscribe(
        () => {
          setAddedRequests((prev) =>
            prev.map((r) => {
              if (r.request_id === requestId) {
                return {
                  ...r,
                  metadata: {
                    ...r.metadata,
                    _maitai_test_request_meta: JSON.stringify({
                      tags: newTags,
                    }),
                  },
                };
              }
              return r;
            })
          );
          setIsAddedLoading(false);
        },
        (error: any) => {
          console.error(error);
          notification.error({
            message: "Error",
            description: "Failed to update tags",
          });
          setIsAddedLoading(false);
        }
      );
    } else {
      setSelectedEligibleRequests((prev) =>
        prev.map((row) => {
          if (row.request_id === requestId) {
            return {
              ...row,
              metadata: {
                ...row.metadata,
                _maitai_test_request_meta: JSON.stringify({ tags: newTags }),
              },
            };
          }
          return row;
        })
      );
    }
  };

  const columns = [
    {
      title: "Date Created",
      dataIndex: "date_created",
      key: "date_created",
      width: "20%",
      render: (text: number) => moment(text).format("YYYY-MM-DD hh:mm:ss A"),
    },
    {
      title: "Tags",
      key: "tags",
      width: "40%",
      render: (_: any, record: ChatCompletionRequest) => {
        let existingTags: string[] = [];
        let suggestedTags: string[] = [];

        if (record.metadata?._maitai_test_request_meta) {
          try {
            const meta = JSON.parse(
              record.metadata._maitai_test_request_meta as string
            );
            existingTags = meta.tags || [];
          } catch (e) {
            console.error("Error parsing test request metadata:", e);
          }
        }

        if (record.metadata?._maitai_request_meta) {
          try {
            const metadata = JSON.parse(
              record.metadata._maitai_request_meta as string
            );
            suggestedTags = (metadata.tags || []).filter(
              (tag: string) => !existingTags.includes(tag)
            );
          } catch (e) {
            console.error("Error parsing metadata:", e);
          }
        }

        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <Select
              mode="tags"
              style={{ width: "100%" }}
              placeholder="Add tags"
              value={existingTags}
              tokenSeparators={[","]}
              options={tagDistribution.map(({ tag }) => ({
                label: tag,
                value: tag,
              }))}
              onChange={(newTags) =>
                handleTagsChange(record.request_id, newTags)
              }
              maxTagTextLength={10}
              maxTagCount={5}
            />
            {suggestedTags.length > 0 && (
              <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                {suggestedTags.map((tag) => (
                  <Tag
                    key={tag}
                    style={{
                      background: "#f0f5ff",
                      borderColor: "#d6e4ff",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      const newTags = [...existingTags, tag];
                      handleTagsChange(record.request_id, newTags);
                    }}>
                    {tag} <PlusOutlined style={{ color: "#1890ff" }} />
                  </Tag>
                ))}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Request ID",
      key: "request_id",
      width: "25%",
      render: (_: any, record: ChatCompletionRequest) => {
        const requestId = record.request_id || "";
        const truncatedId = `...${requestId.slice(-8)}`;
        return (
          <Space style={{ width: "100%" }}>
            {truncatedId}
            <Button
              icon={<CopyOutlined />}
              size="small"
              type="text"
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(requestId);
                message.success("Request ID copied to clipboard");
              }}
            />
          </Space>
        );
      },
    },
    {
      title: "",
      key: "actions",
      width: "15%",
      render: (_: any, record: ChatCompletionRequest) => (
        <Space>
          <RequestViewButton
            buttonStyle={{ color: "#21b892" }}
            to={`/application/${
              record.application_id
            }/session/${encodeURIComponent(record.session_id)}/request/${
              record.request_id
            }`}
            request={record}
            apiKey={apiKey}
            isEditable={true}
            testSetId={testSetId}
            testSetRequestId={
              record.metadata?._maitai_test_request_id as string
            }
            onUpdated={(updatedRequest) =>
              handleRequestSave(updatedRequest, "added")
            }>
            View
          </RequestViewButton>
          <TestRequestEdit
            request={record}
            onSave={(updatedRequest) =>
              handleRequestSave(updatedRequest, "added")
            }
            testSetId={testSetId}
            testSetRequestId={
              record.metadata?._maitai_test_request_id as string
            }
            apiKey={apiKey}
          />
        </Space>
      ),
    },
  ];

  const eligibleColumns = [
    {
      title: "Date Created",
      dataIndex: "date_created",
      key: "date_created",
      width: "20%",
      render: (text: number) => moment(text).format("YYYY-MM-DD hh:mm:ss A"),
    },
    {
      title: "Tags",
      key: "tags",
      width: "40%",
      render: (_: any, record: ChatCompletionRequest) => {
        const isSelected = selectedEligibleRequests.some(
          (r) => r.request_id === record.request_id
        );
        const selectedRow = selectedEligibleRequests.find(
          (r) => r.request_id === record.request_id
        );
        let tags: string[] = [];
        let metadataTags: string[] = [];

        if (selectedRow?.metadata?._maitai_test_request_meta) {
          try {
            const meta = JSON.parse(
              selectedRow.metadata._maitai_test_request_meta as string
            );
            tags = meta.tags || [];
          } catch (e) {
            console.error("Error parsing metadata:", e);
          }
        }

        if (record.metadata?._maitai_request_meta) {
          try {
            const metadata = JSON.parse(
              record.metadata._maitai_request_meta as string
            );
            metadataTags = metadata.tags || [];
          } catch (e) {
            console.error("Error parsing metadata:", e);
          }
        }

        // Combine existing tag distribution with metadata tags for options
        const allTagOptions = Array.from(
          new Set([...tagDistribution.map(({ tag }) => tag), ...metadataTags])
        ).map((tag) => ({ label: tag, value: tag }));

        return isSelected ? (
          <Select
            mode="tags"
            style={{ width: "100%" }}
            placeholder="Add tags"
            value={tags}
            tokenSeparators={[","]}
            options={allTagOptions}
            onClick={(e) => e.stopPropagation()}
            maxTagTextLength={10}
            maxTagCount={5}
            onChange={(newTags) => {
              const updatedRequests = selectedEligibleRequests.map((row) => {
                if (row.request_id === record.request_id) {
                  return {
                    ...row,
                    metadata: {
                      ...row.metadata,
                      _maitai_test_request_meta: JSON.stringify({
                        tags: newTags,
                      }),
                    },
                  };
                }
                return row;
              });
              setSelectedEligibleRequests(updatedRequests);
            }}
          />
        ) : (
          <span
            style={{ color: "#999", cursor: "pointer" }}
            onClick={(e) => {
              e.stopPropagation();
              const newRequest = {
                ...record,
                metadata: {
                  ...record.metadata,
                  _maitai_test_request_meta: JSON.stringify({ tags: [] }),
                },
              };
              setSelectedEligibleRequests([
                ...selectedEligibleRequests,
                newRequest,
              ]);
            }}>
            {metadataTags.length > 0 ? (
              <Space size={[0, 4]} wrap>
                {metadataTags.map((tag) => (
                  <Tag key={tag}>{tag}</Tag>
                ))}
              </Space>
            ) : (
              "Select request to add tags"
            )}
          </span>
        );
      },
    },
    {
      title: "Request ID",
      key: "request_id",
      width: "25%",
      render: (_: any, record: ChatCompletionRequest) => {
        const requestId = record.request_id || "";
        const truncatedId = `...${requestId.slice(-8)}`;
        return (
          <Space style={{ width: "100%" }}>
            {truncatedId}
            <Button
              icon={<CopyOutlined />}
              size="small"
              type="text"
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(requestId);
                message.success("Request ID copied to clipboard");
              }}
            />
          </Space>
        );
      },
    },
    {
      title: "",
      key: "actions",
      width: "15%",
      render: (_: any, record: ChatCompletionRequest) => (
        <Space>
          <RequestViewButton
            buttonStyle={{ color: "#21b892" }}
            request={record}
            apiKey={apiKey}
            isEditable={true}
            testSetId={testSetId}
            onUpdated={(updatedRequest) =>
              handleRequestSave(updatedRequest, "eligible")
            }
            to={`/application/${
              record.application_id
            }/session/${encodeURIComponent(record.session_id)}/request/${
              record.request_id
            }`}>
            View
          </RequestViewButton>
          <TestRequestEdit
            request={record}
            onSave={(updatedRequest) =>
              handleRequestSave(updatedRequest, "eligible")
            }
            isEligible={true}
            onSelect={() => {
              const newRequest = {
                ...record,
                metadata: {
                  ...record.metadata,
                  _maitai_test_request_meta: JSON.stringify({ tags: [] }),
                },
              };
              setSelectedEligibleRequests((prev) => {
                const existingIndex = prev.findIndex(
                  (r) => r.request_id === record.request_id
                );
                if (existingIndex >= 0) {
                  // Replace the existing entry
                  const updated = [...prev];
                  updated[existingIndex] = newRequest;
                  return updated;
                }
                // Add as new entry
                return [...prev, newRequest];
              });
            }}
            apiKey={apiKey}
          />
        </Space>
      ),
    },
  ];

  const handleAddSelected = () => {
    setIsAddedLoading(true);
    setIsEligibleLoading(true);
    const payload = {
      test_set_id: Number(testSetId),
      requests: selectedEligibleRequests.map((request) => ({
        request_id: request.request_id,
        metadata: request.metadata || {},
      })),
    };

    TestSetService.addTestSetRequests(payload, null, apiKey).subscribe(
      () => {
        addedRequestsCacheRef.current = {};
        eligibleRequestsCacheRef.current = {};

        setSelectedEligibleRequests([]);
        setCurrentAddedPage(1);
        setCurrentEligiblePage(1);
        setAddedRefreshTrigger((prev) => prev + 1);
        setEligibleRefreshTrigger((prev) => prev + 1);
        setIsAddedLoading(false);
        setIsEligibleLoading(false);
        message.success(
          `${selectedEligibleRequests.length} requests added successfully`
        );
      },
      (error: any) => {
        console.error(error);
        notification.error({
          message: "Error",
          description: `Failed to add requests: ${error}`,
        });
        setIsAddedLoading(false);
        setIsEligibleLoading(false);
      }
    );
  };

  const handleRemoveSelected = () => {
    setIsAddedLoading(true);
    setIsEligibleLoading(true);

    const payload = {
      test_set_id: Number(testSetId),
      requests: selectedAddedRequests.map((request) => ({
        request_id: request.request_id,
        metadata: request.metadata || {},
      })),
    };

    TestSetService.removeTestSetRequests(payload, null, apiKey).subscribe(
      () => {
        addedRequestsCacheRef.current = {};
        eligibleRequestsCacheRef.current = {};

        setSelectedAddedRequests([]);
        setCurrentAddedPage(1);
        setCurrentEligiblePage(1);
        setAddedRefreshTrigger((prev) => prev + 1);
        setEligibleRefreshTrigger((prev) => prev + 1);
        setIsAddedLoading(false);
        setIsEligibleLoading(false);
        message.success(
          `${selectedAddedRequests.length} requests removed successfully`
        );
      },
      (error: any) => {
        console.error(error);
        notification.error({
          message: "Error",
          description: `Failed to remove requests: ${error}`,
        });
        setIsAddedLoading(false);
        setIsEligibleLoading(false);
      }
    );
  };

  const items = [
    {
      key: "eligible",
      label: "Eligible Requests",
      children: (
        <>
          <div
            style={{
              display: "flex",
              gap: "8px",
              justifyContent: "space-between",
              marginBottom: 16,
            }}>
            <div
              style={{ display: "flex", gap: "8px", flexDirection: "column" }}>
              <div style={{ display: "flex", gap: "8px" }}>
                <Space.Compact style={{ width: "300px" }}>
                  <Input
                    placeholder="Search by query..."
                    value={queryFilter}
                    onChange={(e) => {
                      setQueryFilter(e.target.value);
                      if (e.target.value === "") {
                        clearSearch();
                      }
                    }}
                    onPressEnter={handleQuerySearch}
                    allowClear
                    disabled={isSearchLoading}
                  />
                  <Button
                    type="primary"
                    onClick={handleQuerySearch}
                    loading={isSearchLoading}
                    disabled={!queryFilter.trim()}>
                    <SearchOutlined />
                  </Button>
                </Space.Compact>

                <Space.Compact style={{ width: "300px" }}>
                  <Input
                    placeholder="Search for session..."
                    value={sessionIdFilter}
                    onChange={(e) => {
                      setSessionIdFilter(e.target.value);
                      if (e.target.value === "" && appliedSessionIdFilter) {
                        // If the input is cleared and there was a filter applied
                        setAppliedSessionIdFilter("");
                        setCurrentEligiblePage(1);
                        eligibleRequestsCacheRef.current = {};
                        setEligibleRefreshTrigger((prev) => prev + 1);
                      }
                    }}
                    onPressEnter={handleSearch}
                    allowClear
                    disabled={isSearchMode || isSearchLoading}
                  />
                  <Button
                    type="primary"
                    onClick={handleSearch}
                    disabled={
                      isSearchMode || isSearchLoading || !sessionIdFilter.trim()
                    }>
                    <SearchOutlined />
                  </Button>
                </Space.Compact>

                {(isSearchMode || appliedSessionIdFilter) && (
                  <Button
                    onClick={clearSearch}
                    icon={<ClearOutlined />}
                    disabled={isSearchLoading}>
                    Clear Search
                  </Button>
                )}
              </div>

              {isSearchMode && searchResults.length > 0 && (
                <div style={{ marginTop: "4px" }}>
                  <Tag color="blue">
                    Found {searchResults.length} results for query: "
                    {queryFilter}"
                  </Tag>
                </div>
              )}

              {appliedSessionIdFilter && !isSearchMode && (
                <div style={{ marginTop: "4px" }}>
                  <Tag color="blue">
                    Filtering by session: "{appliedSessionIdFilter}"
                  </Tag>
                </div>
              )}
            </div>

            {selectedEligibleRequests.length > 0 && (
              <Button
                type="primary"
                onClick={handleAddSelected}
                loading={isEligibleLoading}
                disabled={isEligibleLoading}>
                Add Selected ({selectedEligibleRequests.length})
              </Button>
            )}
          </div>
          <div
            style={{
              marginBottom: 16,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
            }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <Switch
                checked={showOnlySelectedEligible}
                onChange={setShowOnlySelectedEligible}
                style={{ marginRight: 8 }}
              />
              <span>Show Only Selected</span>
            </div>
          </div>
          <Table
            rowKey="request_id"
            columns={eligibleColumns}
            dataSource={
              showOnlySelectedEligible
                ? selectedEligibleRequests
                : eligibleRequests
            }
            loading={isEligibleLoading || isSearchLoading}
            pagination={{
              current: currentEligiblePage,
              pageSize: eligiblePageSize,
              total: showOnlySelectedEligible
                ? selectedEligibleRequests.length
                : isSearchMode
                ? searchResults.length
                : totalEligible,
              onChange: (page, pageSize) => {
                if (isSearchMode) {
                  // In search mode, handle pagination differently
                  handleSearchPagination(page, pageSize || eligiblePageSize);
                  return;
                }

                setCurrentEligiblePage(page);
                if (pageSize && pageSize !== eligiblePageSize) {
                  setEligiblePageSize(pageSize);
                  setEligibleRequests([]);
                  eligibleRequestsCacheRef.current = {};
                }
              },
              showSizeChanger: !isSearchMode,
              pageSizeOptions: ["10", "20", "50", "100"],
              onShowSizeChange: (current, size) => {
                if (isSearchMode) return;

                setCurrentEligiblePage(1);
                setEligiblePageSize(size);
                setEligibleRequests([]);
                eligibleRequestsCacheRef.current = {};
              },
            }}
            rowSelection={{
              selectedRowKeys: selectedEligibleRequests.map(
                (r) => r.request_id
              ),
              onChange: (_, selectedRows) => {
                const updatedSelectedRows = selectedRows.map((newRow) => {
                  const existingRow = selectedEligibleRequests.find(
                    (r) => r.request_id === newRow.request_id
                  );
                  return (
                    existingRow || {
                      ...newRow,
                      metadata: {
                        ...newRow.metadata,
                        _maitai_test_request_meta: JSON.stringify({ tags: [] }),
                      },
                    }
                  );
                });
                setSelectedEligibleRequests(updatedSelectedRows);
              },
            }}
          />
        </>
      ),
    },
    {
      key: "added",
      label: "Added Requests",
      children: (
        <>
          <div
            style={{
              marginBottom: 16,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}>
            <div>
              {selectedAddedRequests.length > 0 && (
                <Button
                  type="primary"
                  danger
                  onClick={handleRemoveSelected}
                  loading={isAddedLoading}
                  disabled={isAddedLoading}>
                  Remove Selected ({selectedAddedRequests.length})
                </Button>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center" }}>
              <Switch
                checked={showOnlySelectedAdded}
                onChange={setShowOnlySelectedAdded}
                style={{ marginRight: 8 }}
              />
              <span>Show Only Selected</span>
            </div>
          </div>
          <Table
            rowKey="request_id"
            columns={columns}
            dataSource={
              showOnlySelectedAdded ? selectedAddedRequests : addedRequests
            }
            loading={isAddedLoading}
            pagination={{
              current: currentAddedPage,
              pageSize: addedPageSize,
              total: showOnlySelectedAdded
                ? selectedAddedRequests.length
                : totalAdded,
              onChange: (page, pageSize) => {
                setCurrentAddedPage(page);
                if (pageSize) {
                  setAddedPageSize(pageSize);
                  setAddedRequests([]);
                  addedRequestsCacheRef.current = {};
                }
              },
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              onShowSizeChange: (current, size) => {
                setCurrentAddedPage(1);
                setAddedPageSize(size);
                setAddedRequests([]);
                addedRequestsCacheRef.current = {};
              },
            }}
            rowSelection={{
              selectedRowKeys: selectedAddedRequests.map((r) => r.request_id),
              onChange: (_, selectedRows) => {
                setSelectedAddedRequests(selectedRows);
              },
              defaultSelectedRowKeys: addedRequests.map((r) => r.request_id),
            }}
          />
        </>
      ),
    },
  ];

  const handleSave = async () => {
    if (hasSelectedRows) {
      message.warning(
        "Please add or remove selected requests before saving changes"
      );
      return;
    }
    try {
      const values = await form.validateFields();
      setIsSaving(true);

      const payload = {
        id: Number(testSetId),
        name: values.name,
        description: values.description,
        is_golden: values.is_golden,
        company_id: testSet?.company_id,
        intent_group_id: Number(intentGroupId),
        date_created: testSet?.date_created,
        meta: testSet?.meta,
      };

      TestSetService.updateTestSet(payload, setIsSaving, apiKey).subscribe(
        () => {
          notification.success({
            message: "Success",
            description: "Test set updated successfully",
          });
          navigate(
            `/intent-group/${testSet.intent_group_id}/testsets/${testSetId}`
          );
        },
        (error: any) => {
          console.error(error);
          notification.error({
            message: "Error",
            description: `Failed to update test set: ${error}`,
          });
          setIsSaving(false);
        }
      );
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  const hasSelectedRows =
    selectedEligibleRequests.length > 0 || selectedAddedRequests.length > 0;

  const handleClearSelected = () => {
    setSelectedEligibleRequests([]);
    setSelectedAddedRequests([]);
  };

  const handleRequestSave = (
    updatedRequest: ChatCompletionRequest,
    table: "eligible" | "added"
  ) => {
    if (table === "eligible") {
      setEligibleRequests((prev) =>
        prev.map((request) =>
          request.request_id === updatedRequest.request_id
            ? updatedRequest
            : request
        )
      );

      if (eligibleRequestsCacheRef.current[currentEligiblePage]) {
        eligibleRequestsCacheRef.current[currentEligiblePage] =
          eligibleRequestsCacheRef.current[currentEligiblePage].map((request) =>
            request.request_id === updatedRequest.request_id
              ? updatedRequest
              : request
          );
      }
    } else {
      setAddedRequests((prev) =>
        prev.map((request) =>
          request.request_id === updatedRequest.request_id
            ? updatedRequest
            : request
        )
      );

      if (addedRequestsCacheRef.current[currentAddedPage]) {
        addedRequestsCacheRef.current[currentAddedPage] =
          addedRequestsCacheRef.current[currentAddedPage].map((request) =>
            request.request_id === updatedRequest.request_id
              ? updatedRequest
              : request
          );
      }
    }
  };

  // Add this function to handle pagination of search results
  const handleSearchPagination = (page: number, pageSize: number) => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, searchResults.length);
    setEligibleRequests(searchResults.slice(startIndex, endIndex));
    setCurrentEligiblePage(page);
  };

  return (
    <Card loading={isLoading}>
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <div
          style={{ fontSize: "24px", fontWeight: "600", marginBottom: "24px" }}>
          {!intentGroup || !testSet ? (
            "Loading..."
          ) : (
            <>
              <Link to={"/intent-groups"} style={{ color: "#828282" }}>
                Intent Groups
                {" / "}
              </Link>
              <Link
                to={`/intent-group/${testSet.intent_group_id}`}
                style={{ color: "#828282" }}>
                {intentGroup.intent_name}
                {" / "}
              </Link>
              <Link
                to={`/intent-group/${intentGroupId}/testsets`}
                style={{ color: "#828282" }}>
                Test Sets{" / "}
              </Link>
              <Link
                to={`/intent-group/${intentGroupId}/testsets/${testSetId}`}
                style={{ color: "#828282" }}>
                {testSet.name}
                {" / "}
              </Link>
              Edit
            </>
          )}
        </div>

        <Form form={form} layout="vertical" style={{ maxWidth: "100%" }}>
          <div
            style={{ display: "flex", gap: "24px", alignItems: "flex-start" }}>
            <Form.Item
              name="name"
              label={<span style={{ fontWeight: 600 }}>Name</span>}
              rules={[{ required: true, message: "Please enter a name" }]}
              style={{ flex: 1 }}>
              <Input placeholder="Enter test set name" />
            </Form.Item>

            <Form.Item
              label={
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontWeight: 600,
                  }}>
                  Golden
                  <Tooltip title="Test sets marked as 'Golden' will be used to evaluate the performance of models after fine-tuning">
                    <InfoCircleOutlined
                      style={{ color: "rgba(0, 0, 0, 0.45)" }}
                    />
                  </Tooltip>
                </div>
              }>
              <Form.Item name="is_golden" valuePropName="checked" noStyle>
                <Switch />
              </Form.Item>
            </Form.Item>
          </div>

          <Form.Item
            name="description"
            label={<span style={{ fontWeight: 600 }}>Description</span>}>
            <Input.TextArea placeholder="Enter test set description" rows={4} />
          </Form.Item>

          {tagDistribution.length > 0 && (
            <Form.Item
              label={
                <span style={{ fontWeight: 600 }}>Request Distribution</span>
              }>
              <Space wrap>
                {tagDistribution.map(({ tag, count }) => (
                  <Tag key={tag} bordered={true}>
                    {tag} ({count})
                  </Tag>
                ))}
              </Space>
            </Form.Item>
          )}
        </Form>

        <Tabs items={items} activeKey={activeTab} onChange={setActiveTab} />

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "8px",
            marginTop: "24px",
          }}>
          {hasSelectedRows && (
            <Button onClick={handleClearSelected}>Clear Selected</Button>
          )}
          <Button
            onClick={() =>
              navigate(
                `/intent-group/${testSet.intent_group_id}/testsets/${testSetId}`
              )
            }>
            Cancel
          </Button>
          <Tooltip
            title={
              hasSelectedRows
                ? "Please add or remove selected requests before saving changes"
                : ""
            }>
            <Button
              type="primary"
              onClick={handleSave}
              loading={isSaving}
              disabled={hasSelectedRows}>
              Save Changes
            </Button>
          </Tooltip>
        </div>
      </Space>
    </Card>
  );
};

export default TestSetEdit;
